import DropBox from "../../component/DropBox";
import PopupPagination from "../../component/Pagination/popupPagination";
import {
  PubMissionLogType,
  SellerPubMissionLogCategoryFilter,
  SellerPubMissionLogTypeFilter,
} from "../../api/pub-mission-log";
import { useEventLogs } from "./hooks/use-event-logs";
import * as S from "./styles";

const CATEGORY_FILTER_LIST = [
  { name: "미션·빙고 전체", value: SellerPubMissionLogCategoryFilter.ALL },
  { name: "미션", value: SellerPubMissionLogCategoryFilter.MISSION },
  { name: "빙고", value: SellerPubMissionLogCategoryFilter.BINGO },
];

const LOG_TYPE_FILTER_LIST = [
  { name: "처리 유형 전체", value: SellerPubMissionLogTypeFilter.ALL },
  { name: "진행도 증가", value: SellerPubMissionLogTypeFilter.PROGRESS_INCREASE },
  { name: "진행도 감소", value: SellerPubMissionLogTypeFilter.PROGRESS_DECREASE },
  { name: "완료", value: SellerPubMissionLogTypeFilter.COMPLETE },
  { name: "완료 취소", value: SellerPubMissionLogTypeFilter.CANCEL_COMPLETE },
  {
    name: "빙고 줄 달성",
    value: SellerPubMissionLogTypeFilter.BINGO_LINE_ACHIEVE,
  },
  {
    name: "빙고 줄 달성 취소",
    value: SellerPubMissionLogTypeFilter.CANCEL_BINGO_LINE_ACHIEVE,
  },
  {
    name: "기타 보상 지급",
    value: SellerPubMissionLogTypeFilter.OTHER_REWARD_GRANT,
  },
  {
    name: "기타 보상 지급 취소",
    value: SellerPubMissionLogTypeFilter.CANCEL_OTHER_REWARD_GRANT,
  },
  {
    name: "매장이용권 지급",
    value: SellerPubMissionLogTypeFilter.STORE_VOUCHER_GRANT,
  },
  {
    name: "매장이용권 지급 취소",
    value: SellerPubMissionLogTypeFilter.CANCEL_STORE_VOUCHER_GRANT,
  },
  { name: "삭제", value: SellerPubMissionLogTypeFilter.DELETE },
];

const COLUMNS = [
  { header: "처리 일시", className: "createdAt" },
  { header: "닉네임", className: "nickname" },
  { header: "미션·빙고명", className: "missionName" },
  { header: "처리 유형", className: "logType" },
  { header: "값", className: "value" },
  { header: "취소 사유", className: "memo" },
  { header: "처리자", className: "processor" },
];

const POSITIVE_LOG_TYPES = new Set<PubMissionLogType>([
  PubMissionLogType.COMPLETE,
  PubMissionLogType.BINGO_LINE_ACHIEVE,
  PubMissionLogType.OTHER_REWARD_GRANT,
  PubMissionLogType.STORE_VOUCHER_GRANT,
]);

const CANCEL_LOG_TYPES = new Set<PubMissionLogType>([
  PubMissionLogType.CANCEL_COMPLETE,
  PubMissionLogType.CANCEL_BINGO_LINE_ACHIEVE,
  PubMissionLogType.CANCEL_OTHER_REWARD_GRANT,
  PubMissionLogType.CANCEL_STORE_VOUCHER_GRANT,
  PubMissionLogType.DELETE,
]);

const getLogTypeClassName = (logType: PubMissionLogType) => {
  if (POSITIVE_LOG_TYPES.has(logType)) return "positive";
  if (CANCEL_LOG_TYPES.has(logType)) return "cancel";
  return "";
};

const EventLogs = () => {
  const {
    list,
    loading,
    totalPages,
    currentPage,
    setCurrentPage,
    startDate,
    endDate,
    nicknameInput,
    setNicknameInput,
    processorInput,
    setProcessorInput,
    category,
    logType,
    handleCategoryChange,
    handleLogTypeChange,
    handleStartDateChange,
    handleEndDateChange,
    applyTextFilters,
    downloadExcel,
    excelDownloading,
  } = useEventLogs();

  return (
    <S.PageWrapper>
      <S.PageHeaderArea>
        <S.Title>처리 내역</S.Title>
      </S.PageHeaderArea>

      <S.ContentScrollArea>
        <S.ContentBody>
          <S.TableSection>
            <S.TableWrapper>
              <S.FilterRow>
                <S.DateRange>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    max={endDate || undefined}
                  />
                  <span className="separator">-</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    min={startDate || undefined}
                  />
                </S.DateRange>

                <S.TextField>
                  <input
                    type="text"
                    placeholder="닉네임"
                    value={nicknameInput}
                    onChange={(e) => setNicknameInput(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") applyTextFilters();
                    }}
                  />
                </S.TextField>

                <DropBox
                  list={CATEGORY_FILTER_LIST}
                  value={category}
                  setValue={handleCategoryChange}
                  width="150px"
                />

                <DropBox
                  list={LOG_TYPE_FILTER_LIST}
                  value={logType}
                  setValue={handleLogTypeChange}
                  width="170px"
                />

                <S.TextField>
                  <input
                    type="text"
                    placeholder="처리자"
                    value={processorInput}
                    onChange={(e) => setProcessorInput(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") applyTextFilters();
                    }}
                  />
                </S.TextField>

                <S.ExcelButton
                  type="button"
                  onClick={downloadExcel}
                  disabled={excelDownloading}
                >
                  {excelDownloading ? "다운로드 중..." : "엑셀 다운로드"}
                </S.ExcelButton>
              </S.FilterRow>

              <S.TableContent>
                <S.TableHeader>
                  {COLUMNS.map((column) => (
                    <div
                      key={column.className}
                      className={`item ${column.className}`}
                    >
                      {column.header}
                    </div>
                  ))}
                </S.TableHeader>

                <S.ListWrapper>
                  {loading ? (
                    <S.EmptyMessage>불러오는 중...</S.EmptyMessage>
                  ) : list.length === 0 ? (
                    <S.EmptyMessage>처리 내역이 없습니다.</S.EmptyMessage>
                  ) : (
                    list.map((item) => (
                      <S.TableItem key={item.id}>
                        <div className="item createdAt">{item.createdAt}</div>
                        <div className="item nickname">{item.nickname}</div>
                        <div className="item missionName">
                          {item.missionName}
                        </div>
                        <div
                          className={`item logType ${getLogTypeClassName(
                            item.logType,
                          )}`}
                        >
                          {item.logTypeLabel}
                        </div>
                        <div className="item value">{item.value ?? "-"}</div>
                        <div className="item memo">{item.memo ?? "-"}</div>
                        <div className="item processor">
                          {item.processorNickname ?? "-"}
                        </div>
                      </S.TableItem>
                    ))
                  )}
                </S.ListWrapper>
              </S.TableContent>

              <S.PaginationBox>
                <PopupPagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </S.PaginationBox>
            </S.TableWrapper>
          </S.TableSection>
        </S.ContentBody>
      </S.ContentScrollArea>
    </S.PageWrapper>
  );
};

export default EventLogs;
